import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SyndromeCharacteristics_new.css';
import '../styles/theme/toast.css';

import { getCaracteristicas, createCaracteristica, updateCaracteristica, getCaracteristicaById, deleteCaracteristica } from '../services/caracteristicasService';

const SyndromeCharacteristics = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [characteristics, setCharacteristics] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [currentCharacteristic, setCurrentCharacteristic] = useState(null);
  const [formData, setFormData] = useState({ name: '', observation: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCharacteristics(page, pageSize);
  }, [page, pageSize]);

  const fetchCharacteristics = async (p = 1, ps = 10) => {
    try {
      setLoading(true);
      const res = await getCaracteristicas(p, ps);
      // Si la API devuelve paginado: { data, total, page, pageSize, totalPages }
      if (res.data && res.data.data !== undefined) {
        const arr = Array.isArray(res.data.data) ? res.data.data : [];
        setCharacteristics(arr.map(item => ({
          id: item.CARACTERISTICA_ID || item.id || '',
          name: item.CARACTERISTICA_NOMBRE || item.name || '',
          observation: item.CARACTERISTICA_OBSERVACION || item.observation || ''
        })));
        setTotal(res.data.total || 0);
        setPage(res.data.page || p);
        setPageSize(res.data.pageSize || ps);
        setTotalPages(res.data.totalPages || Math.max(1, Math.ceil((res.data.total || 0) / (res.data.pageSize || ps))));
      } else {
        // Fallback: API devuelve lista completa
        const data = Array.isArray(res.data) ? res.data : [];
        setCharacteristics(data.map(item => ({
          id: item.CARACTERISTICA_ID || item.id || '',
          name: item.CARACTERISTICA_NOMBRE || item.name || '',
          observation: item.CARACTERISTICA_OBSERVACION || item.observation || ''
        })));
        setTotal(data.length);
        setTotalPages(1);
        setPage(1);
      }
    } catch (error) {
      setCharacteristics([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre de la característica es requerido';
    } else if (formData.name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }
    if (!formData.observation.trim()) {
      newErrors.observation = 'La observación es requerida';
    } else if (formData.observation.length < 10) {
      newErrors.observation = 'La observación debe tener al menos 10 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCreate = () => {
    setFormData({ name: '', observation: '' });
    setCurrentCharacteristic(null);
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = async (characteristic) => {
    try {
      const res = await getCaracteristicaById(characteristic.id);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      setFormData({
        name: data.CARACTERISTICA_NOMBRE || '',
        observation: data.CARACTERISTICA_OBSERVACION || ''
      });
      setCurrentCharacteristic(characteristic);
      setErrors({});
      setShowForm(true);
    } catch (error) {
      alert('Error al cargar la característica');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta característica?')) {
      try {
        await deleteCaracteristica(id);
        alert('Característica eliminada exitosamente');
        fetchCharacteristics();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Error al eliminar la característica';
        alert(errorMessage);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (currentCharacteristic) {
        // Actualizar característica existente
        await updateCaracteristica(currentCharacteristic.id, {
          CARACTERISTICA_NOMBRE: formData.name,
          CARACTERISTICA_OBSERVACION: formData.observation
        });
        alert('Característica actualizada exitosamente');
      } else {
        // Crear nueva característica
        await createCaracteristica({
          CARACTERISTICA_NOMBRE: formData.name,
          CARACTERISTICA_OBSERVACION: formData.observation
        });
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      }
      setLoading(false);
      await fetchCharacteristics();
      resetForm();
    } catch (err) {
      setLoading(false);
      setErrors({ api: err.response?.data?.message || 'Error al guardar característica' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', observation: '' });
    setCurrentCharacteristic(null);
    setShowForm(false);
    setErrors({});
  };

  const filteredCharacteristics = characteristics.filter(char => 
    char.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    char.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {showToast && (
        <div className="custom-toast custom-toast-success">
          ¡Característica registrada exitosamente!
        </div>
      )}
    <div className="syndrome-characteristics-container">
      <div className="page-header">
        <div className="header-title">
          <h1>Gestión de Características</h1>
          <p>Administra las características médicas del síndrome de Turner</p>
        </div>
      </div>

      <div className="characteristics-actions">
        <div className="search-section">
          <div className="search-input">
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={searchTerm}
              onChange={handleSearch}
            />
            <button type="button" title="Buscar">
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        
        <button className="btn-primary create-btn" onClick={handleCreate}>
          <i className="fas fa-plus"></i>
          Registrar Característica
        </button>
      </div>

      <div className="characteristics-table-container">
        <table className="characteristics-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Observación</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredCharacteristics.map(char => (
              <tr key={char.id}>
                <td><span className="code-id-field">{char.id}</span></td>
                <td>{char.name}</td>
                <td>{char.observation}</td>
                <td className="actions-cell">
                  <button 
                    className="action-btn"
                    onClick={() => handleEdit(char)}
                    title="Editar"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="action-btn"
                    onClick={() => handleDelete(char.id)}
                    title="Eliminar"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        {loading ? (
          <div>Cargando...</div>
        ) : (
          <>
            <div>
              Mostrando <strong>{filteredCharacteristics.length}</strong> de <strong>{total}</strong> características
            </div>
            <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
              <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
              <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
              <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Por página:</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </label>
            </div>
          </>
        )}
      </div>
    </div>

        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <i className="fas fa-dna"></i>
                  {currentCharacteristic ? 'Editar Característica' : 'Registrar Característica'}
                </h3>
                <button className="modal-close" onClick={resetForm}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form className="profession-form" onSubmit={handleSubmit}>
                <div className="form-section">
                  <h3>Información de la Característica</h3>
                  <div className="form-group">
                    <label htmlFor="name">
                      Nombre de la Característica <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? 'error' : ''}
                      placeholder="Ej: Alta Estatura"
                      disabled={loading}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="observation">
                      Observación <span className="required">*</span>
                    </label>
                    <textarea
                      id="observation"
                      name="observation"
                      value={formData.observation}
                      onChange={handleChange}
                      className={errors.observation ? 'error' : ''}
                      placeholder="Describe la característica..."
                      rows="4"
                      disabled={loading}
                    />
                    {errors.observation && <span className="error-message">{errors.observation}</span>}
                  </div>
                </div>
                {errors.api && <div className="form-error">{errors.api}</div>}
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={resetForm} disabled={loading}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Guardando...
                      </>
                    ) : (
                      currentCharacteristic ? 'Guardar Cambios' : 'Registrar Característica'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </>
  );
};

export default SyndromeCharacteristics;